import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
    TextField, Button, Box, Typography, Paper,
    Alert, CircularProgress, Select, MenuItem,
    FormControl, InputLabel
} from '@mui/material';
import stateDistricts from '../data/stateDistricts';
import CompletionMeter from '../components/common/CompletionMeter';
import { isValidIndianPhoneNumber, isValidEmail } from '../utils/validation';

function PostFoundPage() {
    const { token } = useAuth();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        itemName: '', description: '', extraPoints: '', phone: '', whatsapp: '', email: '',
        state: '', district: '', category: ''
    });
    const [image, setImage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [completion, setCompletion] = useState(0);
    const [categories, setCategories] = useState([]);
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const { data } = await axios.get('/api/categories');
                setCategories(data);
            } catch (error) {
                console.error("Could not fetch categories");
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        const fields = {
            itemName: { value: formData.itemName, weight: 20 },
            description: { value: formData.description, weight: 20 },
            image: { value: image, weight: 20 },
            state: { value: formData.state, weight: 10 },
            district: { value: formData.district, weight: 10 },
            category: { value: formData.category, weight: 10 },
            phone: { value: formData.phone, weight: 5 },
            email: { value: formData.email, weight: 5 },
        };
        let currentScore = 0;
        for (const key in fields) {
            if (fields[key].value) {
                currentScore += fields[key].weight;
            }
        }
        setCompletion(currentScore);
    }, [formData, image]);

    const validateForm = () => {
        const newErrors = {};
        const { phone, whatsapp, email, state, extraPoints, district, category } = formData;



        if (!phone && !whatsapp && !email) {
            newErrors.contact = 'Please provide at least one contact method (Phone, WhatsApp, or Email).';
        }
        if (phone && !isValidIndianPhoneNumber(phone)) {
            newErrors.phone = 'Please enter a valid 10-digit Indian mobile number.';
        }
        if (whatsapp && !isValidIndianPhoneNumber(whatsapp)) {
            newErrors.whatsapp = 'Please enter a valid 10-digit WhatsApp number.';
        }
        if (email && !isValidEmail(email)) {
            newErrors.email = 'Please enter a valid email address.';
        }
        if (!state) {
            newErrors.state = 'State is required.';
        }
        if (!district) {
            newErrors.district = 'District is required.';
        }
        if (!formData.extraPoints.trim()) {
            newErrors.extraPoints = 'Identifying marks are required.';
        }
        if (!category) {
            newErrors.category = 'Category is required.';
        }

        if (!image) {
            newErrors.image = 'Image upload is required.';
        }

        setFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleImageChange = (e) => {
        setImage(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!validateForm()) return;

        setLoading(true);

        const postData = new FormData();
        Object.keys(formData).forEach(key => {
            if (key === 'extraPoints') {
                postData.append(key, JSON.stringify(
                    formData[key].split(',').map(s => s.trim()).filter(Boolean)
                ));
            } else {
                postData.append(key, formData[key]);
            }
        });

        postData.append('image', image);

        try {
            const { data } = await axios.post('/api/items/found', postData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                }
            });
            setSuccess(data.message || 'Item posted successfully!');
            setTimeout(() => navigate('/my-found-items'), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to post item.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" gutterBottom>Post a Found Item</Typography>
                <CompletionMeter percentage={completion} />
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            {formErrors.contact && <Alert severity="warning" sx={{ mb: 2 }}>{formErrors.contact}</Alert>}
            {formErrors.image && <Alert severity="warning" sx={{ mb: 2 }}>{formErrors.image}</Alert>}

            <form onSubmit={handleSubmit}>
                <TextField name="itemName" label="Item Name" fullWidth margin="normal" required onChange={handleChange} />
                <TextField name="description" label="Description" fullWidth margin="normal" multiline rows={4} required onChange={handleChange} />

                <Typography sx={{ mt: 2, color: 'text.secondary' }}>Contact Information (Provide at least one)</Typography>
                <TextField
                    name="phone"
                    label="Phone Number"
                    fullWidth
                    margin="normal"
                    onChange={handleChange}
                    error={!!formErrors.phone}
                    helperText={formErrors.phone}
                />
                <TextField
                    name="whatsapp"
                    label="WhatsApp Number"
                    fullWidth
                    margin="normal"
                    onChange={handleChange}
                    error={!!formErrors.whatsapp}
                    helperText={formErrors.whatsapp}
                />
                <TextField
                    name="email"
                    label="Email"
                    type="email"
                    fullWidth
                    margin="normal"
                    onChange={handleChange}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                />
                <TextField
                    name="extraPoints"
                    label="Identifying Marks (comma separated)"
                    fullWidth
                    margin="normal"
                    onChange={handleChange}
                    error={!!formErrors.extraPoints}
                    helperText={formErrors.extraPoints}
                />
                <FormControl fullWidth margin="normal" required error={!!formErrors.category}>
                    <InputLabel>Category</InputLabel>
                    <Select name="category" value={formData.category} label="Category" onChange={handleChange}>
                        {categories.map(cat => (
                            <MenuItem key={cat._id} value={cat.name}>{cat.name}</MenuItem>
                        ))}
                    </Select>
                    {formErrors.category && (
                        <Typography color="error" variant="caption">{formErrors.category}</Typography>
                    )}
                </FormControl>

                <FormControl fullWidth margin="normal" required error={!!formErrors.state}>
                    <InputLabel>State</InputLabel>
                    <Select name="state" value={formData.state} label="State" onChange={handleChange}>
                        {Object.keys(stateDistricts).map(state => (
                            <MenuItem key={state} value={state}>{state}</MenuItem>
                        ))}
                    </Select>
                    {formErrors.state && (
                        <Typography color="error" variant="caption">{formErrors.state}</Typography>
                    )}
                </FormControl>          


                {formData.state && (
                    <FormControl fullWidth margin="normal" required error={!!formErrors.district}>
                        <InputLabel>District</InputLabel>
                        <Select name="district" value={formData.district} label="District" onChange={handleChange}>
                            {stateDistricts[formData.state].map(district => (
                                <MenuItem key={district} value={district}>{district}</MenuItem>
                            ))}
                        </Select>
                        {formErrors.district && (
                            <Typography color="error" variant="caption">{formErrors.district}</Typography>
                        )}
                    </FormControl>
                )}


                <Button variant="contained" component="label" sx={{ mt: 2 }}>
                    Upload Image
                    <input type="file" hidden onChange={handleImageChange} />
                </Button>
                {image && <Typography sx={{ display: 'inline', ml: 2 }}>{image.name}</Typography>}

                <br />
                <Button type="submit" variant="contained" disabled={loading} sx={{ mt: 2 }}>
                    {loading ? <CircularProgress size={24} /> : 'Submit'}
                </Button>
            </form>
        </Paper>
    );
}

export default PostFoundPage;
